import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { Store } from '@ngrx/store';
import { combineLatest, noop, skip, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { addMonths, format, parseISO } from 'date-fns';
import {
  atLeastOnePerson,
  atLeastOneWeekday,
  BASE_DATE_SERVICE_FORMAT,
  BASE_PERMISSIONS,
  BASE_ROLES,
  baseAuthSelectors,
  BaseAvatarSize,
  BaseBusinessKeyPipe,
  BaseButtonSize,
  BaseDateService,
  BaseFormatDecimalPipe,
  baseFormsActions,
  baseFormsSelectors,
  baseInquiryActions,
  baseInquirySelectors,
  BaseInquiryStatus,
  baseIsDeepEqual,
  BaseListPagination,
  BaseModalService,
  BaseRolesService,
  baseSharedActions,
  baseSharedReducer,
  baseSharedSelectors,
  BaseShiftIconsEnum,
  BaseShiftPlanService,
  BaseStaffRequestFormDatesInterface,
  BaseStaffRequestFormInterface,
  BaseTokenInterface,
  BaseWeekDays,
  CustomerDepartmentInterface,
  CustomerDepartmentQualificationsListItemResponse,
  CustomerDepartmentResponse,
  DepartmentListItemResponse,
  dutyDataValidator,
  ENVIRONMENTER,
  flexShiftsAmountValidator,
  FormRawValue,
  getDaysArrayWithWeekdays,
  InquiryCreateRequestExtended,
  InquiryDate,
  InquiryDateExtended,
  InquiryResponse,
  mapDaysOfWeekToNumbers,
  RequirementResponse,
  ResponsibleInterface,
  ShiftsAndPersonsFormInterface,
  ShiftTimesResponse,
  TimeAndShiftsFormInterface,
  UserItemResponse,
  weekdayDateValidator,
} from 'base';
import { DEP_INIT_MPL } from '../../_constants/department-mpl.const';

@Component({
  selector: 'base-customer-staff-request-modal',
  templateUrl: './base-customer-staff-request-modal.component.html',
})
export class BaseCustomerStaffRequestModalComponent implements OnInit, OnDestroy {
  @Input() modalRef: NgbModalRef;
  @Input() inquiry: InquiryResponse;
  @Input() selectedFilters: { [key in any]: any };
  @Input() isInquiryDefaultFilter = false;
  @ViewChild('shiftPlan') shiftPlan: ElementRef;
  @ViewChild('editTimeShifts', { static: true }) editTimeShifts: ElementRef;
  departments: DepartmentListItemResponse[] = [];
  qualifications: CustomerDepartmentQualificationsListItemResponse[] = [];
  requirements: RequirementResponse[];
  users: Array<UserItemResponse & { fullName: string }> = [];
  filteredQualifications: CustomerDepartmentQualificationsListItemResponse[] = [];
  filteredRequirements: RequirementResponse[];
  submitted = false;
  clickedPublish = false;
  clickedShiftView = false;
  requirementsOpen = false;
  isResponsibleLoading = false;
  lastWeekStart = BaseDateService.getStartDayOfLastWeek();
  minDate: NgbDateStruct;
  departmentChangesAreAvailable = true;
  departmentMLP: number | undefined = DEP_INIT_MPL;
  canPublishInquiry: boolean;
  today = new Date();
  form: FormGroup<BaseStaffRequestFormInterface> = this.fb.group({
    dates: this.fb.group({
      range: [this.rangeInitialValue(), Validators.required],
      weekdays: this.fb.group({
        [BaseWeekDays.MO]: [true],
        [BaseWeekDays.TU]: [true],
        [BaseWeekDays.WE]: [true],
        [BaseWeekDays.TH]: [true],
        [BaseWeekDays.FR]: [true],
        [BaseWeekDays.SA]: [true],
        [BaseWeekDays.SU]: [true],
      }),
    }),
    unlimited: [false],
    customerDepartment: [null as CustomerDepartmentInterface | null, Validators.required],
    responsible: [null as ResponsibleInterface | null, Validators.required],
    mainQualification: [null as CustomerDepartmentQualificationsListItemResponse | null, Validators.required],
    jobDescription: ['', Validators.required],
    qualifications: [[] as number[]],
    requirements: [[] as number[]],
    weeklyWorkingHours: [0],
    shiftsAndPersons: this.fb.group({
      shifts: this.fb.group({
        fd: [true],
        zd: [true],
        sd: [true],
        nd: [true],
      }),
      persons: this.fb.group({
        fdPersons: [0],
        zdPersons: [0],
        sdPersons: [0],
        ndPersons: [0],
      }),
      amountPersons: [1],
    }),
    flexShift: [true],
    timeShiftsForm: this.fb.array([] as FormGroup<TimeAndShiftsFormInterface>[]),
    costUnit: [null as string | null, [Validators.maxLength(255)]],
    workEquipmentPsp: [null as string | null, [Validators.maxLength(255)]],
    workEquipmentCustomer: [null as string | null, [Validators.maxLength(255)]],
    remark: [null as string | null],
  });
  cachedFormValue: FormRawValue<FormGroup<BaseStaffRequestFormInterface>>;
  isFirstCustomerDepartmentInit = true;
  isFirstDepartmentInit = true;
  dates: InquiryDateExtended[];
  isCustomerAdmin = false;

  baseShiftIconsEnum = BaseShiftIconsEnum;
  avatarSize = BaseAvatarSize;
  buttonSize = BaseButtonSize;
  timeShiftsModalRef: NgbModalRef;
  destroy$ = new Subject<boolean>();

  private startDateSubscription: Subscription;

  constructor(
    private fb: NonNullableFormBuilder,
    private formatDecimalPipe: BaseFormatDecimalPipe,
    private businessKeyPipe: BaseBusinessKeyPipe,
    private modalService: BaseModalService,
    private router: Router,
    @Inject(ENVIRONMENTER) private environment: any,
    private store: Store<baseSharedReducer.SharedState>,
    private rolesService: BaseRolesService,
    private baseShiftPlanService: BaseShiftPlanService,
    private activatedRoute: ActivatedRoute
  ) {}

  get timeShiftsForm() {
    return this.form.controls.timeShiftsForm as FormArray<FormGroup<TimeAndShiftsFormInterface>>;
  }

  get shiftsAndPersonsFormGroup(): FormGroup<ShiftsAndPersonsFormInterface> {
    return this.form.controls.shiftsAndPersons as FormGroup<ShiftsAndPersonsFormInterface>;
  }

  get datesFormGroup(): FormGroup<BaseStaffRequestFormDatesInterface> {
    return this.form.controls.dates as FormGroup<BaseStaffRequestFormDatesInterface>;
  }

  ngOnInit(): void {
    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);
    this.isCustomerAdmin = this.rolesService.hasRole(BASE_ROLES.CUSTOMER_ADMIN);

    this.minDate = {
      year: this.lastWeekStart.getFullYear(),
      month: this.lastWeekStart.getMonth() + 1,
      day: this.lastWeekStart.getDate(),
    };

    if (this.inquiry) {
      this.updateForms(this.inquiry);
    } else {
      this.store
        .select(baseFormsSelectors.getStaffRequestForm)
        .pipe(
          take(1),
          filter((state) => !!state?.data),
          map((state) => state.data as FormRawValue<FormGroup<BaseStaffRequestFormInterface>>)
        )
        .subscribe((formValue) => {
          this.setSavedFormData(formValue);
        });
    }

    this.initForm();
    this.getInitialTimeShifts();
    this.formDataHandlers();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.store.dispatch(baseInquiryActions.clearInquiryCustomerData());
    if (this.startDateSubscription) {
      this.startDateSubscription.unsubscribe();
    }
  }

  rangeInitialValue() {
    return {
      from: BaseDateService.format(this.today, BASE_DATE_SERVICE_FORMAT),
      to: BaseDateService.format(BaseDateService.addDays(this.today, 1), BASE_DATE_SERVICE_FORMAT),
    };
  }

  calculateUnlimitedDate(startDate: string) {
    return format(addMonths(parseISO(startDate), this.departmentMLP ? this.departmentMLP : DEP_INIT_MPL), 'yyyy-MM-dd');
  }

  setDateOnDepartmentChange() {
    if (this.form.controls.unlimited.value) {
      const unlimitedDate = this.calculateUnlimitedDate(this.form.controls.startDate!.value);
      this.form.controls.dates.controls.range.setValue({
        from: this.form.controls.startDate!.value,
        to: unlimitedDate ?? this.form.controls.dates.controls.range.value.to,
      });
    }
  }

  validateDateRange() {
    const fromDateString = this.form.controls.dates.controls.range.value.from as string;
    const minDateStruct = this.minDate as NgbDateStruct;

    if (fromDateString) {
      const fromDate = parseISO(fromDateString);
      const minDate = new Date(minDateStruct.year, minDateStruct.month - 1, minDateStruct.day);

      if (BaseDateService.isBefore(fromDate, minDate)) {
        if (this.form.value.unlimited) {
          this.form.controls.dates.controls.range.patchValue(this.rangeInitialValue());
          this.form.controls.startDate!.setValue('');
        } else {
          this.form.controls.dates.controls.range.patchValue({ from: '', to: '' });
        }
      }
    }
  }

  checkValidationForPublish(onlySave = false) {
    this.validateDateRange();

    this.shiftsAndPersonsFormGroup.controls.amountPersons.setValidators(
      this.form.value.flexShift ? Validators.min(1) : []
    );
    this.shiftsAndPersonsFormGroup.controls.amountPersons.updateValueAndValidity();
    this.datesFormGroup.controls.weekdays.setValidators(atLeastOneWeekday());
    this.datesFormGroup.controls.weekdays.updateValueAndValidity();
    this.datesFormGroup.setValidators(weekdayDateValidator());
    this.datesFormGroup.updateValueAndValidity();
    this.shiftsAndPersonsFormGroup.controls.shifts.setValidators(
      this.form.value.flexShift ? flexShiftsAmountValidator() : []
    );
    this.shiftsAndPersonsFormGroup.controls.shifts.updateValueAndValidity();
    this.shiftsAndPersonsFormGroup.controls.persons.setValidators(this.form.value.flexShift ? [] : atLeastOnePerson());
    this.shiftsAndPersonsFormGroup.controls.persons.updateValueAndValidity();
    this.form.clearValidators();
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();

    this.submitted = true;
    this.clickedPublish = true;
    this.clickedShiftView = false;

    if (this.form.invalid) {
      return;
    }

    onlySave ? this.saveUnpublishedInquiry() : this.publish();
    this.clearStaffRequestForm();
  }

  checkValidationForShiftView() {
    this.validateDateRange();

    this.shiftsAndPersonsFormGroup.controls.amountPersons.clearValidators();
    this.shiftsAndPersonsFormGroup.controls.amountPersons.updateValueAndValidity();
    this.datesFormGroup.controls.weekdays.clearValidators();
    this.datesFormGroup.controls.weekdays.updateValueAndValidity();
    this.datesFormGroup.setValidators(weekdayDateValidator());
    this.datesFormGroup.updateValueAndValidity();
    this.shiftsAndPersonsFormGroup.controls.shifts.clearValidators();
    this.shiftsAndPersonsFormGroup.controls.shifts.updateValueAndValidity();
    this.shiftsAndPersonsFormGroup.controls.persons.clearValidators();
    this.shiftsAndPersonsFormGroup.controls.persons.updateValueAndValidity();
    this.form.setValidators(dutyDataValidator());
    this.form.updateValueAndValidity();

    this.form.markAllAsTouched();

    this.submitted = true;
    this.clickedPublish = false;
    this.clickedShiftView = true;

    if (this.form.invalid) {
      return;
    }

    this.switchToShiftView();
  }

  openEditTimeShiftsModal() {
    this.timeShiftsModalRef = this.modalService.open(
      {
        headText: this.businessKeyPipe.transform('action.editShiftTimes'),
        bodyTemplate: this.editTimeShifts,
        // width: 600,
        hideFooter: true,
      },
      {
        backdrop: 'static',
      }
    );

    this.timeShiftsModalRef.result.then(noop, noop);
  }

  clearStaffRequestForm(): void {
    this.form.reset();
    this.cachedFormValue = this.form.getRawValue();
    this.store.dispatch(baseFormsActions.clearStaffRequestForm());
    this.form.controls.dates.controls.range.setValue(this.rangeInitialValue());
    this.departmentMLP = DEP_INIT_MPL;
  }

  redirectToManageOrganisation(): void {
    this.modalRef.close();
    this.router.navigate(['/administration/location-management']);
  }

  private updateForms(inquiry: InquiryResponse) {
    const formData = this.baseShiftPlanService.transformInquiryToFormData(inquiry);
    if (formData.unlimited) {
      this.addStartDateControl();
    } else {
      this.removeStartDateControl();
    }

    this.form.patchValue(formData, { emitEvent: false });
    this.baseShiftPlanService.setTimeShifts(this.timeShiftsForm, formData.timeShiftsForm);
  }

  private initForm() {
    this.form.controls.customerDepartment.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value && this.departmentChangesAreAvailable) {
        this.store.dispatch(baseSharedActions.getCustomerDepartmentUsers({ payload: value.id }));
        this.isResponsibleLoading = true;
        this.store.dispatch(
          baseSharedActions.loadCustomerDepartmentQualificationsList({
            payload: { department: value.id, tag: this.environment.application.tag },
          })
        );
        this.store.dispatch(baseInquiryActions.fetchDepartment({ payload: value.id }));
        if (!this.isFirstCustomerDepartmentInit) {
          this.form.controls.responsible.reset();
          this.form.controls.mainQualification.reset();
          this.form.controls.requirements.reset();
        }
        this.isFirstCustomerDepartmentInit = false;
        this.departmentChangesAreAvailable = false;
      }
    });

    this.form.controls.mainQualification.valueChanges.pipe(takeUntil(this.destroy$), skip(1)).subscribe((value) => {
      if (value) {
        this.filteredQualifications = this.filteredQualifications?.filter((item) => {
          return item.id !== this.form.controls.mainQualification.value?.id;
        });

        let selectedQualifications = this.form.controls.qualifications.value || [];
        this.removeMainQualificationFromQualifications(selectedQualifications);
        this.form.controls.jobDescription.setValue(value.description as string);
      } else {
        this.form.controls.jobDescription.reset();
      }
    });

    this.form.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe((_) => {
        this.saveFormsToCache();
      });

    this.form.controls.unlimited.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((unlimited) => {
      if (unlimited) {
        this.addStartDateControl();

        const rangeFrom =
          this.form.controls.dates.controls.range.value.from || format(this.today, BASE_DATE_SERVICE_FORMAT);
        this.form.controls.startDate!.setValue(rangeFrom);
        this.form.controls.flexShift.setValue(true);
      } else {
        this.removeStartDateControl();
      }
      const startDate = this.form.controls.startDate?.value || format(this.today, BASE_DATE_SERVICE_FORMAT);
      const unlimitedDate = this.calculateUnlimitedDate(startDate);

      this.form.controls.dates.controls.range.setValue({
        from: startDate,
        to: unlimitedDate ?? this.form.controls.dates.controls.range.value.to,
      });
    });

    this.form.controls.customerDepartment.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setDateOnDepartmentChange();
    });
  }

  private addStartDateControl() {
    if (!this.form.contains('startDate')) {
      this.form.addControl(
        'startDate',
        new FormControl(format(this.today, 'yyyy-MM-dd'), { nonNullable: true, validators: [Validators.required] })
      );

      this.startDateSubscription = this.form.controls.startDate!.valueChanges.subscribe((startDate) => {
        if (startDate) {
          const unlimitedDate = this.calculateUnlimitedDate(startDate);
          this.form.controls.dates.controls.range.setValue({ from: startDate, to: unlimitedDate });
        }
      });
    }
  }

  private removeStartDateControl() {
    if (this.form.contains('startDate')) {
      this.form.removeControl('startDate');

      this.startDateSubscription.unsubscribe();
    }
  }

  private removeMainQualificationFromQualifications(selectedQualifications: number[]): number[] {
    if (selectedQualifications?.length && this.form.get('mainQualification')?.value?.id) {
      selectedQualifications = selectedQualifications.filter(
        (qualification) => qualification !== this.form.get('mainQualification')?.value?.id
      );
    }

    this.form.get('qualifications')?.setValue(selectedQualifications);
    return selectedQualifications;
  }

  private saveFormsToCache(): void {
    this.store.dispatch(
      baseFormsActions.setStaffRequestForm({
        payload: {
          ...this.form.getRawValue(),
        },
      })
    );
  }

  private prepareOutput(): InquiryCreateRequestExtended {
    const formValue = this.form.getRawValue();

    return {
      unlimited: formValue.unlimited,
      dates: this.prepareDates(),
      shiftTimes: formValue.timeShiftsForm,
      mainQualification: formValue.mainQualification?.id || 0,
      customerDepartment: formValue.customerDepartment?.id || 0,
      responsible: formValue.responsible?.party || 0,
      weeklyWorkingHours: this.formatDecimalPipe.transform(formValue.weeklyWorkingHours || 0).replace(',', '.'),
      requirements: formValue.requirements ? formValue.requirements : [],
      qualifications: formValue.qualifications ? formValue.qualifications : [],
      jobDescription: formValue.jobDescription,
      costUnit: formValue.costUnit || null,
      workEquipmentCustomer: formValue.workEquipmentCustomer || null,
      workEquipmentPsp: formValue.workEquipmentPsp || null,
      remark: formValue.remark || null,
    };
  }

  private updateInquiryList() {
    const queryParams = this.activatedRoute.snapshot.queryParams;

    this.store.dispatch(
      baseInquiryActions.getInquiries({
        payload: { ...queryParams, page: BaseListPagination.page, size: BaseListPagination.size },
      })
    );
  }

  private publish() {
    this.submitted = false;
    this.store.dispatch(
      baseInquiryActions.createAndPublishInquiry({
        payload: {
          data: this.prepareOutput(),
          callback: () => {
            this.updateInquiryList();
          },
        },
      })
    );
    if (this.isInquiryDefaultFilter) {
      this.router.navigate(['inquiries/list'], {
        queryParams: { page: 1, status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` },
      });
    } else {
      this.router.navigate([], {
        queryParams: { ...this.activatedRoute.snapshot.queryParams, page: 1 },
      });
    }
    this.modalRef.close();
  }

  private saveUnpublishedInquiry() {
    this.submitted = false;
    this.store.dispatch(
      baseInquiryActions.createInquiry({
        payload: {
          data: this.prepareOutput(),
          callback: () => {
            this.updateInquiryList();
          },
        },
      })
    );
    if (this.isInquiryDefaultFilter) {
      this.router.navigate(['inquiries/list'], {
        queryParams: { page: 1, size: 15, status: `${BaseInquiryStatus.unpublished},${BaseInquiryStatus.published}` },
      });
    }
    this.modalRef.close();
  }

  private switchToShiftView() {
    this.submitted = false;
    const formValue = this.form.getRawValue();

    this.store.dispatch(
      baseInquiryActions.shiftPlanCreateData({
        payload: {
          calendar: {
            ...formValue,
            shiftsAndPersons: {
              ...formValue.shiftsAndPersons,
              amountPersons: Number(formValue.shiftsAndPersons.amountPersons),
            },
          },
          inquiries: {
            statuses: ['2'],
            includeDates: true,
            current: true,
            mainQualifications: [this.form.value.mainQualification?.id as number],
            departments: [this.form.value.customerDepartment?.id as number],
          },
        },
      })
    );

    this.modalRef.close();
    this.router.navigate(['inquiries/shift-plan']);
  }

  private prepareDates(): InquiryDate[] {
    const weekdays = mapDaysOfWeekToNumbers(this.datesFormGroup.controls.weekdays.getRawValue());
    const formValue = this.form.getRawValue();
    const startDate = formValue.dates.range.from;
    const endDate = formValue.dates.range.to;
    const shifts = formValue.shiftsAndPersons.shifts;
    let dates: string[];

    if (startDate && endDate && weekdays.length === 0) {
      dates = getDaysArrayWithWeekdays<string>(new Date(startDate), new Date(endDate));
    } else {
      dates = endDate
        ? getDaysArrayWithWeekdays<string>(new Date(startDate), new Date(endDate), weekdays)
        : getDaysArrayWithWeekdays<string>(new Date(startDate), BaseDateService.addMonths(this.today, 6), weekdays);
    }

    return dates.map((date: string) => ({
      date,
      flexShift: formValue.flexShift,
      shiftFd: formValue.flexShift
        ? shifts.fd
          ? formValue.shiftsAndPersons.amountPersons
          : 0
        : formValue.shiftsAndPersons.persons.fdPersons,
      shiftZd: formValue.flexShift
        ? shifts.zd
          ? formValue.shiftsAndPersons.amountPersons
          : 0
        : formValue.shiftsAndPersons.persons.zdPersons,
      shiftSd: formValue.flexShift
        ? shifts.sd
          ? formValue.shiftsAndPersons.amountPersons
          : 0
        : formValue.shiftsAndPersons.persons.sdPersons,
      shiftNd: formValue.flexShift
        ? shifts.nd
          ? formValue.shiftsAndPersons.amountPersons
          : 0
        : formValue.shiftsAndPersons.persons.ndPersons,
      //TODO: property 'position' can be removed
      position: 1,
    }));
  }

  private setDepartment(department: DepartmentListItemResponse): void {
    this.form.controls.customerDepartment.setValue(department);
  }

  private setSavedFormData(formValue: FormRawValue<FormGroup<BaseStaffRequestFormInterface>>) {
    let storedFormValue = { ...formValue };
    if (formValue && !baseIsDeepEqual(formValue, this.form.getRawValue())) {
      this.cachedFormValue = formValue;

      if (formValue.unlimited) {
        this.addStartDateControl();
      }

      if (!formValue.dates.range.from || !formValue.dates.range.to) {
        const startDate = formValue.dates.range.from || BaseDateService.format(this.today, BASE_DATE_SERVICE_FORMAT);

        storedFormValue = {
          ...formValue,
          dates: {
            weekdays: formValue.dates.weekdays,
            range: {
              from: startDate,
              to: BaseDateService.format(BaseDateService.addDays(new Date(startDate), 1), BASE_DATE_SERVICE_FORMAT),
            },
          },
        };
      }

      if (BaseDateService.startOfDay(new Date(formValue.dates.range.from)) < BaseDateService.startOfDay(this.today)) {
        storedFormValue = {
          ...formValue,
          dates: {
            weekdays: formValue.dates.weekdays,
            range: {
              from: BaseDateService.format(this.today, BASE_DATE_SERVICE_FORMAT),
              to: BaseDateService.format(BaseDateService.addDays(this.today, 1), BASE_DATE_SERVICE_FORMAT),
            },
          },
        };
      }

      this.form.patchValue(
        {
          ...storedFormValue,
          weeklyWorkingHours: parseFloat(storedFormValue.weeklyWorkingHours as unknown as string),
        },
        { emitEvent: false }
      );
    }
  }

  private formDataHandlers() {
    this.store
      .select(baseSharedSelectors.getDepartments)
      .pipe(
        take(1),
        map((response) => response.data)
      )
      .subscribe((departments) => {
        this.departments = departments || [];
        if (this.departments.length === 1) {
          this.setDepartment(this.departments[0]);
        }
      });

    this.store
      .select(baseSharedSelectors.getRequirements)
      .pipe(
        takeUntil(this.destroy$),
        map((response) => response.data)
      )
      .subscribe((requirements) => {
        if (!requirements) {
          return;
        }
        const reqs = requirements.flatMap((category) => category.requirements);
        this.filteredRequirements = reqs;
        this.requirements = reqs;
      });

    this.store
      .select(baseSharedSelectors.getCustomerDepartmentQualificationsList)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as CustomerDepartmentQualificationsListItemResponse[])
      )
      .subscribe((qualificationsListResponse) => {
        this.filteredQualifications = qualificationsListResponse || [];
        this.qualifications = qualificationsListResponse || [];
      });

    this.store.dispatch(baseSharedActions.clearCustomerDepartmentUsers());
    combineLatest([
      this.store.select(baseSharedSelectors.getDepartmentUsers),
      this.store.select(baseAuthSelectors.getUserInfo),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([departmentUsersState, userState]) => !!departmentUsersState.ok && !!userState.ok),
        map(
          ([departmentUsersState, userState]) =>
            [departmentUsersState.data, userState.data] as [UserItemResponse[], BaseTokenInterface]
        )
      )
      .subscribe(([departmentUsers, loggedInUser]) => {
        this.users = (departmentUsers || []).map((user) => ({
          ...user,
          fullName: `${user.firstName} ${user.lastName}`,
        }));
        const currentUserInDepartment = this.users.find((user) => user.party === loggedInUser.id);
        if (currentUserInDepartment) {
          this.form.controls.responsible.patchValue({
            party: currentUserInDepartment.party,
            fullName: currentUserInDepartment.fullName,
          });
        }
        this.isResponsibleLoading = false;
      });

    this.store
      .select(baseInquirySelectors.getDepartment)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((value) => value.data as CustomerDepartmentResponse)
      )
      .subscribe((department) => {
        this.departmentChangesAreAvailable = true;

        if (!this.inquiry || this.inquiry.customerDepartment?.name !== department?.primaryData.name) {
          this.baseShiftPlanService.setTimeShifts(this.timeShiftsForm, department?.shiftTimes || []);
        }

        this.departmentMLP = department?.location?.maximumLeasePeriodDifference;
        this.setDateOnDepartmentChange();

        this.form.controls.weeklyWorkingHours.setValue(
          this.cachedFormValue && this.cachedFormValue.weeklyWorkingHours
            ? +this.cachedFormValue.weeklyWorkingHours
            : this.form.controls.customerDepartment.value && department?.weeklyWorkingHours
            ? +department.weeklyWorkingHours
            : 0
        );

        if (department?.requirements && !this.isFirstDepartmentInit) {
          this.form.controls.requirements.setValue(
            department?.requirements?.map((requirement) => requirement.id) as number[]
          );
        }

        if (this.cachedFormValue) {
          this.cachedFormValue = {
            ...this.cachedFormValue,
            weeklyWorkingHours: 0,
          };
        }
        this.isFirstDepartmentInit = false;
      });
  }

  private getInitialTimeShifts() {
    if (this.timeShiftsForm.getRawValue().length !== 0) {
      return;
    }

    this.store
      .select(baseSharedSelectors.getDefaultShiftTimes)
      .pipe(
        filter((value) => !!value.ok),
        map((result) => result.data as ShiftTimesResponse),
        take(1)
      )
      .subscribe(({ shiftTimes }) => {
        this.baseShiftPlanService.setTimeShifts(this.timeShiftsForm, shiftTimes);
      });
    this.store.dispatch(baseSharedActions.fetchDefaultShiftTimes());
  }
}
